// Phase HUB-DRILL-1 — Micro Hubs list.
import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Search } from "lucide-react";
import RoleGate from "../../components/RoleGate";
import useTimedFetch from "../../hooks/useTimedFetch";
import { HubDetailError } from "../../components/founder/MainHubDetailView";

function Body() {
  const nav = useNavigate();
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useTimedFetch("/founder/micro-hubs",
    { params: { search: search || undefined, city: city || undefined, status: status || undefined } });
  const cities = useMemo(() => [...new Set((data?.rows || []).map((r) => r.city).filter(Boolean))].sort(), [data]);
  const rows = data?.rows || [];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6" data-testid="founder-micro-hubs-list">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => nav("/dashboard/ceo")}
                className="text-[#FF6B00] hover:opacity-80" data-testid="micro-hubs-back">
          <ArrowLeft className="h-5 w-5"/>
        </button>
        <h1 className="text-2xl font-bold text-slate-900">Micro Hubs</h1>
        <span className="text-sm text-slate-500">· {rows.length} of {data?.total ?? 0}</span>
        <button onClick={reload} className="ml-auto text-slate-400 hover:text-[#FF6B00]"
                aria-label="Refresh" data-testid="micro-hubs-refresh">
          <RefreshCw className="h-4 w-4"/>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400"/>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search name or ID"
                 data-testid="micro-hubs-search"
                 className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:border-[#FF6B00] focus:outline-none"/>
        </div>
        <select value={city} onChange={(e) => setCity(e.target.value)}
                data-testid="micro-hubs-city-filter"
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5">
          <option value="">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="micro-hubs-status-filter"
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending_approval">Pending approval</option>
        </select>
      </div>

      {error && <HubDetailError {...error} onRetry={reload}/>}
      {loading && !data && (
        <div className="space-y-2" data-testid="micro-hubs-loading">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse"/>)}
        </div>
      )}

      {data && rows.length === 0 && !loading && (
        <div className="text-center text-slate-400 py-12" data-testid="micro-hubs-empty">
          No micro hubs match the filters.
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-slate-50/70">
              <tr className="text-left text-slate-500">
                <Th>Micro hub</Th>
                <Th>ID</Th>
                <Th>Parent main hub</Th>
                <Th>Lead</Th>
                <Th className="text-right">Packers</Th>
                <Th className="text-right">Orders today</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.hub_id}
                    data-testid={`micro-hub-row-${r.hub_id}`}
                    className="hover:bg-orange-50/40">
                  <td className="px-3 py-2 font-semibold">
                    <Link to={`/dashboard/founder/micro-hubs/${r.hub_id}`}
                          className="text-[#C2410C] hover:underline">{r.name || "—"}</Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.hub_id}</td>
                  <td className="px-3 py-2">
                    {r.parent_hub_id
                      ? <Link to={`/dashboard/founder/main-hubs/${r.parent_hub_id}`}
                              className="text-[#C2410C] hover:underline">
                          {r.parent_hub_name || r.parent_hub_id}
                        </Link>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">{r.lead_name || <span className="text-slate-400 italic">Unassigned</span>}</td>
                  <td className="px-3 py-2 text-right">{r.packers_count}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[#FF6B00]">{r.orders_today}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${
                      r.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : "bg-slate-100 text-slate-600 border-slate-200"
                    }`}>{r.status || "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 font-semibold uppercase tracking-wider text-[10px] ${className}`}>{children}</th>;
}

export default function MicroHubsList() {
  return (
    <RoleGate allow={["founder", "admin", "main_hub_manager", "ceo"]}>
      <Body/>
    </RoleGate>
  );
}
